import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { parse, differenceInDays, isValid } from 'date-fns';
import { excelStyle } from '../excelStyle.class';
import * as XLSXStyle from 'xlsx-js-style';

@Injectable({
  providedIn: 'root'
})

export class ExportExcelService {

  public exportToExcelByProp(objectList: any, columnNames?: string[], filename?: string): void {
    import('xlsx').then(xlsx => {
      const newArray: any[] = [];
      objectList.forEach((obj, index) => {
        const tempObj = {};
        columnNames.forEach(column => {
          tempObj[column] = obj[column];

        });
        newArray.push(tempObj);
      });
      const worksheet = xlsx.utils.json_to_sheet(newArray);
      this.autoFitColumns(newArray, worksheet);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      this.saveAsExcelFile(excelBuffer, filename);
    });
  }

  public exportToExcel(objectList: any, columnNames?: any, filename?: string, groupByResults = false) {
    import('xlsx').then(xlsx => {
      const newArray: any[] = [];
      if (!groupByResults) {
        objectList.forEach((obj, index) => {
          const o = {};
          columnNames.forEach(c => {
            const value = obj[c.field];
            const description = c.header;
            o[description] = value;
          });
          newArray.push(o);
        });
      } else {
        objectList.forEach((obj, index) => {
          const o = {};
          columnNames.forEach(c => {
            let value;
            if (c.header === 'Count') {
              value = obj.count;
            } else {
              value = obj.header[c.field];
            }
            const description = c.header;
            o[description] = value;
          });
          newArray.push(o);
        });
      }

      const worksheet = xlsx.utils.json_to_sheet(newArray);
      this.autoFitColumns(newArray, worksheet);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array', cellStyles: true });
      this.saveAsExcelFile(excelBuffer, filename);
    });
  }

  public exportTrainingMatrix(objectList: any, columnNames?: any, filename?: string, groupByResults = false) {
    import('xlsx-js-style').then(xlsx => {
      const newArray: any[] = [];
      if (!groupByResults) {
        objectList.forEach((obj, index) => {
          const o = {};
          columnNames.forEach(c => {
            const value = obj[c.field];
            const description = c.header;
            o[description] = value;
          });
          newArray.push(o);
        });
      } else {
        objectList.forEach((obj, index) => {
          const o = {};
          columnNames.forEach(c => {
            let value;
            if (c.header === 'Count') {
              value = obj.count;
            } else {
              value = obj.header[c.field];
            }
            const description = c.header;
            o[description] = value;
          });
          newArray.push(o);
        });
      }

      const data = newArray.map(obj => Object.values(obj));//convert to array of arrays so that data keys are not used as headers

      //Create header rows
      const titleRow1 = new Array(data[0].length);
      titleRow1[0] = filename; //Title
      const titleRow2 = new Array(data[0].length);
      titleRow2[0] = 'Exported from ECA eCOMS on ' + new Date().toDateString();//export info
      titleRow2[5] = 'Expired'; //colour key
      titleRow2[6] = 'Due <30 Days'; //colour key
      titleRow2[7] = 'Due >30 Days'; //colour key
      const titleRow3 = new Array(data[0].length); //blank
      data.unshift(titleRow3);
      data.unshift(titleRow2);
      data.unshift(titleRow1);

      //Create Worksheet
      const worksheet = xlsx.utils.aoa_to_sheet(data);// use aoa instead of Json to stop json key headers being added automatically

      //Apply the styling
      this.addFilter(worksheet, data, 4);//apply filter to column headers (2nd row)      
      this.setDateStyle(worksheet, 30, 0); //apply styles to the cells based on days difference

      const myNoExpirystyle = this.styleBuilder(new excelStyle());
      myNoExpirystyle.fill.fgColor.rgb = styleColors.Green;
      myNoExpirystyle.alignment.horizontal = 'center';
      this.setTextStyle(worksheet, 'No Expiry', myNoExpirystyle); //set No Expiry colour

      const myTitlestyle = this.styleBuilder(new excelStyle());
      myTitlestyle.fill.fgColor.rgb = styleColors.SteelBlue;
      myTitlestyle.font.bold = true;
      myTitlestyle.font.sz = '16';
      myTitlestyle.font.color.rgb = styleColors.White;
      this.setRowStyle(worksheet, [0], myTitlestyle); //set title colour
      this.mergeCells(worksheet, [0, 2], [0, 0]);//merge title cells

      const mySubTitlestyle = this.styleBuilder(new excelStyle());
      mySubTitlestyle.fill.fgColor.rgb = styleColors.SteelBlue;
      mySubTitlestyle.font.color.rgb = styleColors.White;
      this.setRowStyle(worksheet, [1, 2], mySubTitlestyle); //set title colour
      this.mergeCells(worksheet, [0, 2], [1, 1]);//merge sub title cells

      const myExpiredStyle = this.styleBuilder(new excelStyle());
      myExpiredStyle.fill.fgColor.rgb = styleColors.Red;
      myExpiredStyle.alignment.horizontal = 'center';
      this.setCellStyle(worksheet, 1, 5, myExpiredStyle); //set Expired Key colour
      const myWarningStyle = this.styleBuilder(new excelStyle());
      myWarningStyle.fill.fgColor.rgb = styleColors.Yellow;
      myWarningStyle.alignment.horizontal = 'center';
      this.setCellStyle(worksheet, 1, 6, myWarningStyle); //set Warning Key colour
      const myValidStyle = this.styleBuilder(new excelStyle());
      myValidStyle.fill.fgColor.rgb = styleColors.Green;
      myValidStyle.alignment.horizontal = 'center';
      this.setCellStyle(worksheet, 1, 7, myValidStyle); //set Valid Key colour

      const myHeaderstyle = this.styleBuilder(new excelStyle());
      myHeaderstyle.fill.fgColor.rgb = styleColors.LightGrey;
      myHeaderstyle.alignment.textRotation = 45;
      this.setRowStyle(worksheet, [3], myHeaderstyle); //set column headers colour

      const myHVstyle = this.styleBuilder(new excelStyle());
      myHVstyle.fill.fgColor.rgb = styleColors.CornflowerBlue;
      this.setTextStyle(worksheet, 'High Voltage', myHVstyle); //set HV colour


      const myLVStyle = this.styleBuilder(new excelStyle());
      myLVStyle.fill.fgColor.rgb = styleColors.LightBlue;
      this.setTextStyle(worksheet, 'Low Voltage', myLVStyle); //set LV color

      const myNEStyle = this.styleBuilder(new excelStyle());
      myNEStyle.fill.fgColor.rgb = styleColors.LightsteelBlue;
      this.setTextStyle(worksheet, 'Non Electrical', myNEStyle); //set NE colour

      const autoHeaders = new Array('Operative Name', 'Business Unit', 'Operative Type', 'Job Title');
      this.autoFitColumns(newArray, worksheet, autoHeaders); //Auto fit width of first 4 columns

      const myColumnArray = Array.from({ length: 75 }, (v, i) => i + 4);
      this.setColumnWidth(worksheet, myColumnArray, 14); //set columns 5-79 to a width of 12

      //Export the workbook
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array', cellStyles: true });
      this.saveAsExcelFile(excelBuffer, filename);
    });
  }

  private saveAsExcelFile(buffer: any, fileName: string): Promise<any> {
    return import('file-saver').then(FileSaver => {
      const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const EXCEL_EXTENSION = '.xlsx';
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
      });
      saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
    });
  }

  private setDateStyle(worksheet: any, expiryAge: number, warningAge: number) {
    //Set the styles
    const expiredStyle = this.styleBuilder(new excelStyle());
    expiredStyle.fill.fgColor.rgb = styleColors.Red;
    expiredStyle.alignment.horizontal = 'center'

    const warningStyle = this.styleBuilder(new excelStyle());
    warningStyle.fill.fgColor.rgb = styleColors.Yellow;
    warningStyle.alignment.horizontal = 'center'

    const validStyle = this.styleBuilder(new excelStyle());
    validStyle.fill.fgColor.rgb = styleColors.Green;
    validStyle.alignment.horizontal = 'center'

    //Basic Loop throught the worksheet and apply styling based on whether it can pass it as a date
    const todaysDate = new Date(); //Todays Date
    for (const element in worksheet) {
      let data = worksheet[element].v;
      //const parsedDate = parse(data, 'dd-MM-yyyy', new Date());//Try to parse the data as a date
      const parsedDate = parse(data, 'yyyy-MM-dd', new Date());//Try to parse the data as a date
      if (isValid(parsedDate)) { //valid date
        const daysDifference = differenceInDays(parsedDate, todaysDate); // Calculate the difference in days (negative days are in the past)
        if (daysDifference > expiryAge) //more than 30 days in the future
        {
          worksheet[element].s = validStyle;
        }
        else if (daysDifference >= warningAge && daysDifference <= expiryAge) //due in next 30 days
        {
          worksheet[element].s = warningStyle;
        }
        else if (daysDifference < warningAge) //in the past
        {
          worksheet[element].s = expiredStyle;
        }
      }
    }
  }

  private setTextStyle(worksheet: any, text: string, style: any) {
    for (const element in worksheet) {
      let data = worksheet[element].v;
      if (data == text) {
        worksheet[element].s = style;
      }
    }
  }

  private setCellStyle(worksheet: any, rowNumber: number, colNumber: number, style: any) {
    const cellAddress = XLSXStyle.utils.encode_cell({ r: rowNumber, c: colNumber });
    if (!worksheet[cellAddress]) worksheet[cellAddress] = { v: '' }; // Create the cell if it doesn't exist
    worksheet[cellAddress].s = style;
  }

  private setRowStyle(worksheet: any, rowNumbers: number[], style: any) {
    const range = XLSXStyle.utils.decode_range(worksheet['!ref']);
    rowNumbers.forEach(function (element, index, array) {
      for (let colNum = range.s.c; colNum <= range.e.c; colNum++) {
        const cellAddress = XLSXStyle.utils.encode_cell({ r: element, c: colNum });
        if (!worksheet[cellAddress]) worksheet[cellAddress] = { v: '' }; // Create the cell if it doesn't exist
        worksheet[cellAddress].s = style;
      }
    });
  }

  private setColumnStyle(worksheet: any, colNumber: number, style: any) {
    const range = XLSXStyle.utils.decode_range(worksheet['!ref']);
    for (let rowNum = range.s.r; rowNum <= range.e.c; rowNum++) {
      const cellAddress = XLSXStyle.utils.encode_cell({ r: rowNum, c: colNumber });
      if (!worksheet[cellAddress]) worksheet[cellAddress] = { v: '' }; // Create the cell if it doesn't exist
      worksheet[cellAddress].s = style;
    }
  }

  private setColumnWidth(worksheet: any, colNumber: number[], newWidth: number) {
    if (!worksheet['!cols']) {
      worksheet['!cols'] = [];
    }
    colNumber.forEach(function (element, index, array) {
      worksheet['!cols'][element] = { width: newWidth };// Set the width for the specified column
    })
  }

  private autoFitColumns(json: any[], worksheet: any, header?: string[]) {
    const jsonKeys = header ? header : Object.keys(json[0]);
    const objectMaxLength = [];
    for (let i = 0; i < json.length; i++) {
      const value = json[i];
      for (let j = 0; j < jsonKeys.length; j++) {
        if (typeof value[jsonKeys[j]] === 'number') {
          objectMaxLength[j] = 10;
        } else {
          const l = value[jsonKeys[j]] ? value[jsonKeys[j]].length : 0;
          objectMaxLength[j] = objectMaxLength[j] >= l ? objectMaxLength[j] : l;
        }
      }
      const key = jsonKeys;
      for (let j = 0; j < key.length; j++) {
        objectMaxLength[j] =
          objectMaxLength[j] >= key[j].length
            ? objectMaxLength[j]
            : key[j].length;
      }
    }
    worksheet['!cols'] = objectMaxLength.map(w => ({ width: w }));
  }

  private mergeCells(worksheet: any, colNumbers: number[], rowNumbers: number[]) {
    if (!worksheet['!merges']) {
      worksheet['!merges'] = [];
    }
    const mergeCount = worksheet['!merges'].length;
    worksheet['!merges'][mergeCount] = { s: { r: rowNumbers[0], c: colNumbers[0] }, e: { r: rowNumbers[1], c: colNumbers[1] } };
  }

  private styleBuilder(style: excelStyle) {
    return {
      alignment: {
        vertical: style.excelStyleAlignment.vertical,
        horizontal: style.excelStyleAlignment.horizontal,
        wrapText: style.excelStyleAlignment.wrapText,
        textRotation: style.excelStyleAlignment.textRotation,
      },
      fill: {
        fgColor:
        {
          rgb: style.excelStyleFill.fgColor.rgb,
        },
        bgColor:
        {
          rgb: style.excelStyleFill.bgColor.rgb,
        }
      },
      font: {
        bold: style.excelStyleFont.bold,
        color:
        {
          rgb: style.excelStyleFont.color.rgb,
        },
        italic: style.excelStyleFont.italic,
        sz: style.excelStyleFont.sz
      }
    }
  }

  private addFilter(worksheet: any, data: any, rowNumber: number) {
    const lastRow = data.length; // The last row index
 
    let lastColumn = data[0].length;
    let filterIndex = "";           //find column index by column number
    while (lastColumn > 0) {
      let remainder = (lastColumn - 1) % 26;
      filterIndex = String.fromCharCode(65 + remainder) + filterIndex;
      lastColumn = Math.floor((lastColumn - 1) / 26);
    }

    const filterRange = `A${rowNumber}:${filterIndex}${lastRow}`;          //set up the filter rande
    worksheet['!autofilter'] = { ref: filterRange };      // Apply autofilter to the determined range
  }
}





enum styleColors {
  Red = 'FF6D6D',
  Green = '70AD47',
  Yellow = 'FFDD71',
  SteelBlue = '44546A',
  CornflowerBlue = '8EA9DB',
  LightBlue = 'B4C6E7',
  LightsteelBlue = 'D9E1F2',
  LightGrey = 'D3D3D3',
  White = 'FFFFFF'
}
